import { ApexApiError } from 'apex-ui-services'
import { AxiosResponse } from 'axios'
import { find } from 'lodash'
import { Dispatch } from 'redux'
import { getAssetMetadata, getCaseAssets, getStatefulProperties } from '../api'
import { HttpResponseStatusCodes } from '../constants'
import { AlpsAssetWithMarginCode } from '../types/Asset'
import {
  AlpsAcatDetailV2WebDto,
  AlpsAsset,
  AlpsAssetMetadata,
  AlpsStatefulProperty,
} from '../api/types'
import {
  ASSET_RETRIEVAL_FAILED,
  CASH_MARGIN_SHORT_CODE_PROPERTY_IDENTIFIER,
  TEMPORARILY_UNAVAILABLE,
} from './constants'

export const LOAD_ASSETS = 'LOAD_ASSETS'
export const LOAD_ASSETS_SUCCESS = 'LOAD_ASSETS_SUCCESS'
export const LOAD_ASSETS_FAILURE = 'LOAD_ASSETS_FAILURE'

const handleError = (
  dispatch: Dispatch,
  error: ApexApiError,
  onError: (error: string) => void
) => {
  const httpResponseStatus = error.response?.status
  const errorMessage =
    httpResponseStatus &&
    httpResponseStatus >= HttpResponseStatusCodes.InternalServerError
      ? TEMPORARILY_UNAVAILABLE
      : ASSET_RETRIEVAL_FAILED
  dispatch({ type: LOAD_ASSETS_FAILURE })
  onError(errorMessage)
}

export const loadAssets = (
  dispatch: Dispatch,
  transferDetails: AlpsAcatDetailV2WebDto,
  onError: (error: string) => void
) => {
  dispatch({ type: LOAD_ASSETS })
  const caseId = transferDetails.processingCaseId

  getCaseAssets(caseId)
    .then((response: AxiosResponse) => {
      const alpsAssets: AlpsAsset[] = response.data
      const assetMetadataPromises: Promise<AxiosResponse>[] = alpsAssets.map(
        alpsAsset => getAssetMetadata(caseId, alpsAsset.id)
      )
      const statefulPropertiesPromises: Promise<AxiosResponse>[] = []

      Promise.all(assetMetadataPromises)
        .then(assetMetadataResponses => {
          assetMetadataResponses.forEach(
            (assetMetadataResponse, assetIndex) => {
              const alpsAssetMetadataList: AlpsAssetMetadata[] =
                assetMetadataResponse.data
              const assetInputId: number | undefined = find(
                alpsAssetMetadataList,
                assetMetadata => !!assetMetadata.systemAction
              )?.id

              statefulPropertiesPromises.push(
                assetInputId
                  ? getStatefulProperties(
                      caseId,
                      alpsAssets[assetIndex].id,
                      assetInputId
                    )
                  : Promise.resolve({
                      data: [],
                      status: 200,
                      statusText: '',
                      headers: null,
                      config: {},
                    })
              )
            }
          )
          Promise.all(statefulPropertiesPromises)
            .then(statefulPropertiesResponses => {
              const assets: AlpsAssetWithMarginCode[] = []

              statefulPropertiesResponses.forEach(
                (statefulPropertiesResponse, assetIndex) => {
                  const statefulProperties: AlpsStatefulProperty[] =
                    statefulPropertiesResponse.data
                  const cashMarginShortCode = find(statefulProperties, {
                    propertyIdentifier: CASH_MARGIN_SHORT_CODE_PROPERTY_IDENTIFIER,
                    state: 'Valid',
                  })
                  const alpsAsset = alpsAssets[assetIndex]
                  assets.push({
                    ...alpsAsset,
                    cashMarginShortCode: cashMarginShortCode?.value || '',
                  })
                }
              )
              dispatch({ type: LOAD_ASSETS_SUCCESS, assets })
            })
            .catch(error => handleError(dispatch, error, onError))
        })
        .catch(error => handleError(dispatch, error, onError))
    })
    .catch(error => handleError(dispatch, error, onError))
}
